// src/components/enhanced-table/EnhancedTable.tsx
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  Box,
  Card,
  Paper,
  Table,
  TableContainer,
  Skeleton,
  Fade,
  Collapse,
  Alert,
  AlertTitle,
  Button,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
  IconButton,
  Popover,
  TextField,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  EnhancedTableProps,
  TableState,
  createSortComparator,
  ITableFilterType,
  TableFilterType,
  EnhancedTableColumnType,
} from './index';
import EnhancedTableHead from './EnhancedTableHead';
import EnhancedTableBody from './EnhancedTableBody';
import EnhancedTablePagination from './EnhancedTablePagination';
import EnhancedTableToolbar from './EnhancedTableToolbar';
import { ErrorOutline, Refresh, Add } from '@mui/icons-material';
import EnhancedTableFilters from './EnhancedTableFilter';
import { useDebounce } from 'src/hooks/useDebounce';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDispatch } from 'react-redux';
import { showAlert } from 'src/redux/alert/alertReducer';
import { useAppDispatch } from 'src/redux/hooks';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import Loader from '../Loader';

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    position: 'relative',
  },
  container: {
    position: 'relative',
    transition: 'all 0.3s ease',
    overflowX: 'hidden',
  },
  boxedContainer: {
    width: '100%',
    margin: '16px auto',
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    padding: theme.spacing(1, 0, 1, 0),
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden', 
    gap: theme.spacing(0.5),
  },
  boxedTable: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '98%',
    margin: '0 auto',
    background: theme.palette.background.paper,
  },
  boxedCell: {
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    boxSizing: 'border-box',
    padding: '6px 8px',
    fontSize: '0.92rem',
  },
  wmsContainer: {
    backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    paddingTop: theme.spacing(1),
    overflow: 'hidden',
  },
  wmsTable: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%',
    background: theme.palette.background.paper,
    '& .MuiTableCell-root': {
      borderRight: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        borderRight: 'none',
      },
    },
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  table: {
    minWidth: 650,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  emptyState: {
    padding: theme.spacing(4, 1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
  },
  errorState: {
    padding: theme.spacing(2),
  },
  filters: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px',
    gap: 8,
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },
  tableActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  titleText: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    letterSpacing: 0.2,
    marginBottom: theme.spacing(0.2),
    lineHeight: 1.1,
  },
  subtitleText: {
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.2),
  },
  divider: {
    width: '100%',
    height: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    border: 'none',
    borderRadius: 2,
    marginBottom: theme.spacing(0.5),
    opacity: 0.10,
  }
}));

// Table state reducer
type TableAction =
  | { type: 'SET_ORDER'; payload: { order: 'asc' | 'desc'; orderBy: string } }
  | { type: 'SET_SELECTED'; payload: string[] }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_ROWS_PER_PAGE'; payload: number }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_FILTER'; payload: { name: string; value: any } }
  | { type: 'SET_FILTERS'; payload: Record<string, any> }
  | { type: 'TOGGLE_COLUMN'; payload: string }
  | { type: 'SET_COLUMN_VISIBILITY'; payload: Record<string, boolean> }
  | { type: 'RESET_FILTERS' }
  | { type: 'SET_GLOBAL_FILTER'; payload: string };

function tableReducer(state: TableState, action: TableAction): TableState {
  switch (action.type) {
    case 'SET_ORDER':
      return { ...state, ...action.payload, page: 0 };
    case 'SET_SELECTED':
      return { ...state, selected: action.payload };
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_ROWS_PER_PAGE':
      return { ...state, rowsPerPage: action.payload, page: 0 };
    case 'SET_SEARCH':
      return { ...state, searchKey: action.payload, page: 0 };
    case 'SET_FILTER':
      return {
        ...state,
        filterValues: { ...state.filterValues, [action.payload.name]: action.payload.value },
        page: 0,
      };
    case 'SET_FILTERS':
      return { ...state, filterValues: action.payload, page: 0 };
    case 'TOGGLE_COLUMN':
      return {
        ...state,
        columnVisibility: {
          ...state.columnVisibility,
          [action.payload]: !state.columnVisibility[action.payload],
        },
      };
    case 'SET_COLUMN_VISIBILITY':
      return { ...state, columnVisibility: action.payload };
    case 'RESET_FILTERS':
      return { ...state, filterValues: {}, searchKey: '', page: 0 };
    case 'SET_GLOBAL_FILTER':
      return { ...state, globalFilter: action.payload, page: 0 };
    default:
      return state;
  }
}

interface ExtendedEnhancedTableProps extends EnhancedTableProps {
  actionsChild?: React.ReactNode;
  rowActionsOutsideMenu?: boolean;
  onAddRow?: (row: any) => Promise<any>;
  onEditRow?: (row: any) => Promise<any>;
  addRowFields?: string[];
  showEditOnTable?: boolean;
  collapsible?: boolean;
  wmsMode?: boolean;
  densityLevel?: 'comfortable' | 'standard' | 'compact';
  boxedCells?: boolean;
  boxedCellClass?: string;
  stripedColors?: string[] | ((theme: any) => string[]);
}

const EnhancedTable: React.FC<ExtendedEnhancedTableProps> = ({
  columns,
  data,
  idField = 'id',
  title,
  subtitle,
  loading = false,
  error = null,
  emptyMessage = 'No data available',
  actions = [],
  filters = [],
  defaultState,
  selection = { showCheckbox: true, selectionMode: 'multiple' },
  dense = false,
  stickyHeader = false,
  maxHeight,
  striped = false,
  bordered = false,
  hoverable = true,
  export: exportOptions = false,
  print = false,
  columnChooser = false,
  fullscreen = false,
  onRowClick,
  onRowDoubleClick,
  onStateChange,
  components = {},
  sx,
  className,
  actionsChild,
  rowActionsOutsideMenu = false,
  onAddRow,
  onEditRow,
  addRowFields,
  showEditOnTable = false,
  collapsible = false,
  wmsMode = false,
  densityLevel = 'standard',
  boxedCells = false,
  boxedCellClass,
  stripedColors,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableRef = useRef<HTMLTableElement>(null);
  const dispatch = useAppDispatch();

  // Initialize state with defaults
  const initialState: TableState = {
    order: 'asc',
    orderBy: '',
    selected: [],
    page: 0,
    rowsPerPage: 10,
    searchKey: '',
    filterValues: {},
    columnVisibility: columns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}),
    globalFilter: '',
    ...defaultState,
  };

  // Use local storage for persistence
  const [savedState, setSavedState] = useLocalStorage<Partial<TableState>>(
    `enhanced-table-${title?.replace(/\s+/g, '-').toLowerCase()}-state`,
    {}
  );

  const [state, tableDispatch] = useReducer(tableReducer, {
    ...initialState,
    ...savedState,
  });

  // Debounced search
  const debouncedSearch = useDebounce(state.searchKey, 300);
  const debouncedGlobalFilter = useDebounce(state.globalFilter, 300);

  // Save state changes to localStorage
  useEffect(() => {
    const { selected, ...stateToSave } = state;
    setSavedState(stateToSave);
    onStateChange?.(state);
  }, [state, setSavedState, onStateChange]);

  // Memoized visible columns
  const visibleColumns = useMemo(
    () => columns.filter((col) => state.columnVisibility[col.id] !== false),
    [columns, state.columnVisibility]
  );

  // Filter function
  const filterData = useCallback(
    (dataToFilter: Record<string, any>) => {
      return Object.entries(dataToFilter).reduce((acc, [key, row]) => {
        // Global search
        if (debouncedGlobalFilter) {
          const searchStr = debouncedGlobalFilter.toLowerCase();
          const matches = Object.values(row).some((value) => {
            if (value == null) return false;
            return String(value).toLowerCase().includes(searchStr);
          });
          if (!matches) return acc;
        }

        // Column-specific search
        if (debouncedSearch) {
          const searchableColumns = columns.filter((col) => col.searchable !== false);
          const matches = searchableColumns.some((col) => {
            const value = row[col.id];
            if (value == null) return false;
            return String(value).toLowerCase().includes(debouncedSearch.toLowerCase());
          });
          if (!matches) return acc;
        }

        // Apply filters
        const passesFilters = filters.every((filter) => {
          const value = row[filter.column];
          const filterValue = state.filterValues[filter.id];

          if (filterValue == null || filterValue === '') return true;

          switch (filter.type) {
            case TableFilterType.SELECT:
            case TableFilterType.MULTISELECT:
              if (Array.isArray(filterValue)) {
                return filterValue.length === 0 || filterValue.includes(value);
              }
              return filterValue === value;

            case TableFilterType.TEXT:
              const textFilter = filter as any;
              const matchMode = textFilter.matchMode || 'contains';
              const valueStr = String(value).toLowerCase();
              const filterStr = String(filterValue).toLowerCase();

              switch (matchMode) {
                case 'startsWith':
                  return valueStr.startsWith(filterStr);
                case 'endsWith':
                  return valueStr.endsWith(filterStr);
                case 'equals':
                  return valueStr === filterStr;
                default:
                  return valueStr.includes(filterStr);
              }

            case TableFilterType.NUMBER:
              const numFilter = filter as any;
              const numValue = Number(value);
              const operator = numFilter.operator || 'equals';

              switch (operator) {
                case 'equals':
                  return numValue === Number(filterValue);
                case 'notEquals':
                  return numValue !== Number(filterValue);
                case 'greaterThan':
                  return numValue > Number(filterValue);
                case 'lessThan':
                  return numValue < Number(filterValue);
                case 'between':
                  return (
                    numValue >= Number(filterValue[0]) && numValue <= Number(filterValue[1])
                  );
                default:
                  return true;
              }

            case TableFilterType.DATE:
            case TableFilterType.DATERANGE:
              // Implement date filtering logic
              return true;

            case TableFilterType.BOOLEAN:
              return value === filterValue;

            case TableFilterType.CUSTOM:
              const customFilter = filter as any;
              return customFilter.filterFn(row, filterValue);

            default:
              return true;
          }
        });

        if (passesFilters) {
          acc[key] = row;
        }
        return acc;
      }, {} as Record<string, any>);
    },
    [columns, filters, state.filterValues, debouncedSearch, debouncedGlobalFilter]
  );

  // Filtered and sorted data
  const processedData = useMemo(() => {
    const filtered = filterData(data);
    const entries = Object.entries(filtered);

    if (state.orderBy) {
      const column = columns.find((col) => col.id === state.orderBy);
      const comparator = createSortComparator(
        state.order,
        state.orderBy,
        column?.type === EnhancedTableColumnType.CUSTOM
          ? { [state.orderBy]: (column as any).sortComparator }
          : undefined
      );
      entries.sort(([, a], [, b]) => comparator(a, b));
    }

    return entries;
  }, [data, filterData, state.order, state.orderBy, columns]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = state.page * state.rowsPerPage;
    const end = start + state.rowsPerPage;
    return Object.fromEntries(processedData.slice(start, end));
  }, [processedData, state.page, state.rowsPerPage]);

  // Handlers
  const handleSort = useCallback((event: React.MouseEvent<unknown>, property: string) => {
    tableDispatch({
      type: 'SET_ORDER',
      payload: {
        order: state.orderBy === property && state.order === 'asc' ? 'desc' : 'asc',
        orderBy: property,
      },
    });
  }, [state.order, state.orderBy]);

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newSelected = processedData.map(([key]) => key);
        tableDispatch({ type: 'SET_SELECTED', payload: newSelected });
      } else {
        tableDispatch({ type: 'SET_SELECTED', payload: [] });
      }
    },
    [processedData]
  );

  const handleSelectOne = useCallback(
    (id: string) => {
      if (selection.selectionMode === 'single') {
        tableDispatch({ type: 'SET_SELECTED', payload: [id] });
      } else {
        const selectedIndex = state.selected.indexOf(id);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
          newSelected = [...state.selected, id];
        } else if (selectedIndex === 0) {
          newSelected = state.selected.slice(1);
        } else if (selectedIndex === state.selected.length - 1) {
          newSelected = state.selected.slice(0, -1);
        } else if (selectedIndex > 0) {
          newSelected = [
            ...state.selected.slice(0, selectedIndex),
            ...state.selected.slice(selectedIndex + 1),
          ];
        }

        tableDispatch({ type: 'SET_SELECTED', payload: newSelected });
      }
    },
    [state.selected, selection.selectionMode]
  );

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    tableDispatch({ type: 'SET_PAGE', payload: newPage });
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    tableDispatch({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) });
  }, []);

  // Loading state
  if (loading && !data) {
    return (
      <Card className={cx(classes.root, className)} sx={sx}>
        <Box p={2}>
          <Skeleton height={60} />
          <Skeleton height={400} />
          <Skeleton height={60} />
        </Box>
      </Card>
    );
  }

  // Error state
  if (error && !data) {
    const ErrorComponent = components.ErrorState;
    if (ErrorComponent) {
      return <ErrorComponent error={error} />;
    }
    return (
      <Card className={cx(classes.root, className)} sx={sx}>
        <Alert
          severity="error"
          className={classes.errorState}
          action={
            <Button color="inherit" size="small" startIcon={<Refresh />}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error Loading Data</AlertTitle>
          {error.message || 'An unexpected error occurred'}
        </Alert>
      </Card>
    );
  }

  const isSelected = (id: string) => state.selected.indexOf(id) !== -1;

  // Inline add/edit state
  const [addingRow, setAddingRow] = React.useState(false);
  const [editingRowId, setEditingRowId] = React.useState<string | null>(null);
  const [editRowValues, setEditRowValues] = React.useState<Record<string, any>>({});
  const [editRowErrors, setEditRowErrors] = React.useState<Record<string, string>>({});
  // Only allow addRowFields (if provided), otherwise all editable columns except actions
  const editableColumns = columns.filter(col => col.editable !== false && col.type !== EnhancedTableColumnType.ACTION);
  const addableColumns = addRowFields
    ? columns.filter(col => addRowFields.includes(col.id) && col.editable !== false && col.type !== EnhancedTableColumnType.ACTION)
    : editableColumns;

  // Validation
  function validateRow(values: Record<string, any>) {
    const errors: Record<string, string> = {};
    (addingRow ? addableColumns : editableColumns).forEach(col => {
      if (col.required && !values[col.id]) {
        errors[col.id] = 'Required';
      }
    });
    return errors;
  }

  // Add row handlers
  const handleAddRowClick = () => {
    setAddingRow(true);
    setEditRowValues(addableColumns.reduce((acc, col) => ({ ...acc, [col.id]: '' }), {}));
    setEditRowErrors({});
  };
  const handleEditRowClick = (rowId: string, row: any) => {
    setEditingRowId(rowId);
    setEditRowValues(editableColumns.reduce((acc, col) => ({ ...acc, [col.id]: row[col.id] }), {}));
    setEditRowErrors({});
  };
  const handleEditInputChange = (colId: string, value: any) => {
    setEditRowValues(prev => ({ ...prev, [colId]: value }));
  };
  const handleEditCancel = () => {
    setAddingRow(false);
    setEditingRowId(null);
    setEditRowValues({});
    setEditRowErrors({});
  };
  const handleEditSave = async () => {
    const errors = validateRow(editRowValues);
    setEditRowErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      if (addingRow && onAddRow) {
        await onAddRow(editRowValues);
        dispatch(showAlert({ message: 'Row added successfully', type: 'success' }));
      } else if (editingRowId && onEditRow) {
        await onEditRow({ ...editRowValues, id: editingRowId });
        dispatch(showAlert({ message: 'Row updated successfully', type: 'success' }));
      }
      handleEditCancel();
    } catch (e: any) {
      dispatch(showAlert({ message: e?.message || 'Error saving row', type: 'error' }));
    }
  };

  const [collapsed, setCollapsed] = React.useState(false);

  // Handler for header click (only if collapsible)
  const handleHeaderClick = (e: React.MouseEvent) => {
    if (!collapsible) return;
    // Don't collapse if clicking on an action or interactive element
    const interactive = (e.target as HTMLElement).closest('button, [role="button"], input, select, textarea, a');
    if (interactive) return;
    setCollapsed((prev) => !prev);
  };

  // Filter popover state
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Check if filters are active
  const filtersActive = Object.keys(state.filterValues).length > 0 || state.searchKey !== '';

  // ✅ NEW: WMS styling helper (consistent with PaginatedTable)
  const getWMSStripedColors = () => {
    if (stripedColors) {
      return typeof stripedColors === 'function' ? stripedColors(theme) : stripedColors;
    }
    if (wmsMode) {
      return theme.palette.mode === 'dark' 
        ? ['#1a1a1a', '#202020'] 
        : ['#ffffff', '#f8f9fa'];
    }
    return ['#fff', '#f7f7fa'];
  };

  return (
    <Box className={wmsMode ? classes.wmsContainer : classes.boxedContainer}>
      <Box className={classes.titleRow}
        style={collapsible ? { cursor: 'pointer', userSelect: 'none' } : {}}
        onClick={handleHeaderClick}
      >
        <Box className={classes.titleSection} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
          {title && (
            <Typography
              variant="h6"
              component="h2"
              data-testid="table-title"
              className={classes.titleText}
            >
              {title}
            </Typography>
          )}
          {subtitle && <span className={classes.subtitleText}>{subtitle}</span>}
          <TextField
            variant="standard"
            placeholder="Search..."
            value={state.searchKey}
            onChange={e => tableDispatch({ type: 'SET_SEARCH', payload: e.target.value })}
            InputProps={{
              disableUnderline: false,
              sx: { fontSize: '0.95rem', minWidth: 160 },
            }}
            sx={{ maxWidth: 240, ml: 2 }}
            size="small"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Filter icon if filters exist */}
          {filters && filters.length > 0 && (
            <Tooltip title="Show filters">
              <span>
                <IconButton
                  onClick={handleFilterClick}
                  color={filtersActive ? "secondary" : "primary"}
                  sx={{
                    bgcolor: filtersActive
                      ? (theme) => theme.palette.secondary.light
                      : (theme) => theme.palette.action.hover,
                    boxShadow: filtersActive ? 2 : undefined,
                    '&:after': filtersActive ? {
                      content: '""',
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: (theme) => theme.palette.secondary.main,
                    } : undefined,
                    '&:hover': {
                      bgcolor: (theme) =>
                        filtersActive
                          ? theme.palette.secondary.main
                          : theme.palette.primary.light,
                      color: (theme) => theme.palette.primary.contrastText,
                    },
                    transition: 'all 0.2s',
                    m: 0,
                    p: 0.5,
                    position: 'relative',
                  }}
                  aria-label="Show filters"
                >
                  <FilterListIcon color={filtersActive ? "secondary" : "inherit"} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {/* Actions area */}
          <Paper
            elevation={2}
            sx={{
              display: 'flex',
              gap: 1,
              p: 0.5,
              borderRadius: 1,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.shadows[1],
              alignItems: 'center',
              minHeight: 36,
            }}
            onClick={e => e.stopPropagation()}
          >
          {actions && actions.length > 0 && actions
          .filter(action => !action.visible || action.visible(state.selected))
          .map((action) => (
            <Tooltip title={action.title || ''} key={action.key} placement="bottom" arrow>
              <span>
                <IconButton
                  color="primary"
                  onClick={() => action.onClick?.(state.selected)}   // ← use current selection
                  disabled={action.disabled ? action.disabled(state.selected) : false}
                  sx={{
                    bgcolor: (theme) => theme.palette.action.hover,
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.primary.light,
                      color: (theme) => theme.palette.primary.contrastText,
                    },
                    transition: 'all 0.2s',
                    m: 0,
                    p: 0.5,
                  }}
                >
                  {action.icon}
                </IconButton>
              </span>
            </Tooltip>
          ))
        }
            {/* Add Row Button */}
            {onAddRow && (
              <Tooltip title="Add Row">
                <span>
                  <IconButton color="primary"  aria-label="Add Row"  data-testid="add-row-btn" onClick={handleAddRowClick} disabled={addingRow || editingRowId !== null} sx={{
                    bgcolor: (theme) => theme.palette.action.hover,
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.primary.light,
                      color: (theme) => theme.palette.primary.contrastText,
                    },
                    transition: 'all 0.2s',
                    m: 0,
                    p: 0.5,
                  }}>
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {(addingRow || editingRowId) && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleEditSave}
                  data-testid="save-row-btn"
                  sx={{ ml: 1 }}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={handleEditCancel}
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </Button>
              </>
            )}
            {actionsChild}
            {collapsible && (
              <IconButton
                size="small"
                sx={{ ml: 1, cursor: 'pointer' }}
                onClick={e => {
                  e.stopPropagation();
                  setCollapsed(prev => !prev);
                }}
                aria-label={collapsed ? 'Expand table' : 'Collapse table'}
              >
                {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            )}
          </Paper>
          {/* Filter popover */}
          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            // open={Boolean(filterAnchorEl)}
            // anchorEl={filterAnchorEl}
            // onClose={handleFilterClose}
            // anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            // transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            // PaperProps={{ sx: { zIndex: 2000, minWidth: 320, maxWidth: 400, p: 2, boxShadow: 3, overflow: 'visible' } }}
          >
            <EnhancedTableFilters
              filters={filters}
              filterValues={state.filterValues}
              onFilterChange={(name, value) => tableDispatch({ type: 'SET_FILTER', payload: { name, value } })}
              onResetFilters={() => tableDispatch({ type: 'RESET_FILTERS' })}
              data={Object.values(data)}
              horizontal
            />
          </Popover>
        </Box>
      </Box>
      <div className={classes.divider} />
      <Collapse in={!collapsed} timeout="auto">
        <Box className={classes.container} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
          {/* Loading overlay */}
          {loading && (
            <Loader />
          )}
          <TableContainer
            sx={{
              maxHeight: maxHeight || (stickyHeader ? 'calc(100vh - 300px)' : 'auto'),
              flex: '1 1 auto',
              minHeight: 0,
            }}
          >
            <Table
              className={wmsMode ? classes.wmsTable : classes.boxedTable}
              aria-labelledby="tableTitle"
              size={densityLevel === 'compact' ? 'small' : 'medium'}
              stickyHeader={stickyHeader}
              ref={tableRef}
            >
              <EnhancedTableHead
                columns={visibleColumns}
                order={state.order}
                orderBy={state.orderBy}
                onSelectAllClick={handleSelectAll}
                onRequestSort={handleSort}
                numSelected={state.selected.length}
                rowCount={processedData.length}
                showCheckbox={selection.showCheckbox}
                selectionMode={selection.selectionMode}
                boxedCells={wmsMode || boxedCells}
                boxedCellClass={boxedCellClass || classes.boxedCell}
              />
              <EnhancedTableBody
                header={visibleColumns}
                data={paginatedData}
                selected={state.selected}
                handleCheckboxClick={handleSelectOne}
                boxedCells={wmsMode || boxedCells}
                boxedCellClass={boxedCellClass || classes.boxedCell}
                striped={striped}
                stripedColors={getWMSStripedColors()}
                actionsColumnId={visibleColumns.find(col => col.type === EnhancedTableColumnType.ACTION)?.id}
                rowActions={actions}
                rowActionsOutsideMenu={rowActionsOutsideMenu}
                // ✅ NEW: WMS props
                wmsMode={wmsMode}
                densityLevel={densityLevel}
                // Inline add/edit props
                addingRow={addingRow}
                editingRowId={editingRowId}
                editRowValues={editRowValues}
                editRowErrors={editRowErrors}
                onEditInputChange={handleEditInputChange}
                onEditCancel={handleEditCancel}
                onEditSave={handleEditSave}
                onEditRowClick={handleEditRowClick}
                addableColumns={addableColumns}
                showEditOnTable={showEditOnTable}
              />
            </Table>
          </TableContainer>
          {processedData.length === 0 && !loading && (
            <Paper
              elevation={3}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 280,
                py: 4,
                px: 2,
                background: (theme) => theme.palette.background.default,
                borderRadius: 2,
                margin: 2
              }}
            >
              {components.EmptyState ? (
                <components.EmptyState />
              ) : (
                <>
                  <ErrorOutline sx={{ fontSize: 64, mb: 2, margin: 2, color: (theme) => theme.palette.primary.light, opacity: 0.7 }} />
                  <Typography variant="h5" gutterBottom color="text.primary" sx={{ fontWeight: 600 }}>
                    {emptyMessage || "No data available"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {state.searchKey || Object.keys(state.filterValues).length > 0
                      ? "Try adjusting your search or filters."
                      : "There is currently no data to display here."}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {(state.searchKey || Object.keys(state.filterValues).length > 0) && (
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => tableDispatch({ type: 'RESET_FILTERS' })}
                      >
                        Clear Filters
                      </Button>
                    )}
                    {onAddRow && (
                      <Button
                        variant="contained"
                        size="medium"
                        startIcon={<AddIcon />}
                        onClick={handleAddRowClick}
                        disabled={addingRow || editingRowId !== null}
                      >
                        Add New
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </Paper>
          )}

          <EnhancedTablePagination
            count={processedData.length}
            rowsPerPage={state.rowsPerPage}
            page={state.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            showFirstButton
            showLastButton
          />
        </Box>
      </Collapse>
    </Box>
  );

  function handleExport() {
    console.log('Export triggered');
  }

  function handlePrint() {
    window.print();
  }

  function handleFullscreen() {
    if (tableRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        tableRef.current.requestFullscreen();
      }
    }
  }
};

export default EnhancedTable;